<template>
  <!-- CONTENT -->
  <div v-shortkey="shortkeys" @shortkey="triggerShortkey" class="p-2">
    <PageTitle v-if="!hideTabs">Cytology Tests</PageTitle>
    <div class="d-flex py-2">
      <button @click="toggleModal" type="button" class="btn btn-primary ml-auto">
        <u>A</u>dd Test(s)
      </button>
    </div>
    <DxGridWithSearch
      :column-auto-width="true"
      :allow-column-resizing="true"
      columnResizingMode="widget"
      @initialized="initializeGrid"
      @content-ready="handleContentReady"
      :selection="selectionMode"
      :autoFocusSearch="false"
      :data-source="source"
      keyExpr="id"
      :columns="columns"
      ref="grid"
      gridName="cytoCaseTestsGrid"
      noDataText="No tests found"
    >
      <template v-slot:actionsTemplate="{ data: { data } }">
        <div class="d-flex justify-content-center">
          <icon-button
            icon="trash-alt"
            class="btn text-danger pointer p-0"
            @click="deleteTest(data.id)"
          />
        </div>
      </template>
    </DxGridWithSearch>
    <Modal :status="isModalOpen" @close="toggleModal">
      <form @submit.prevent.stop="handleAddTest" class="p-4">
        <h3>Add Molecular Test(s)</h3>
        <SelectInput
          required
          ref="selector"
          label="Select Test Or Panel"
          :dataSource="options"
          v-model="targetId"
          searchMode="startsWith"
        />
        <div class="footer">
          <button :disabled="isLoading" class="btn d-inline-flex btn-primary ml-auto">
            <Loader size="small" v-if="isLoading" />
            <span v-else>Save</span>
          </button>
        </div>
      </form>
    </Modal>
  </div>
</template>

<script>
import { SpecimensApi } from "@/services";
import { mapState } from "vuex";
import PageTitle from "@/components/common/PageTitle.vue";
import Modal from "@/components/common/Modal.vue";
import SelectInput from "@/components/common/SelectInput.vue";
import cytologyService, { CytologyEntities } from "@/services/cytology";
import { CytMolecTestStatusEnum, enumToDropDown } from "@/modules/enums";
import IconButton from "@/components/common/IconButton.vue";
import { formatDatetimeCell, getAltKeys } from "@/modules/helpers";
import DataSource from "devextreme/data/data_source";
import Loader from "./common/Loader.vue";
import { switchMap, map, distinctUntilChanged } from "rxjs/operators";
import DxGridWithSearch from "./common/DxGridWithSearch.vue";

export default {
  components: { PageTitle, Modal, SelectInput, IconButton, Loader, DxGridWithSearch },
  props: {
    caseIdentifier: {
      required: false
    },
    accession: {
      type: Boolean,
      default: false
    },
    specimenIdentifier: {
      required: false
    },
    hideTabs: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isModalOpen: false,
      isLoading: false,
      testOptions: cytologyService.createSearch(CytologyEntities.CytMolecTests),
      molecularPanelOptions: cytologyService.createSearch(CytologyEntities.CytMolecPanels),
      statusOptions: enumToDropDown(CytMolecTestStatusEnum),
      molecTests: {},
      store: cytologyService.getLabCytoTests(),
      shortkeys: getAltKeys("as"),
      targetId: null,
      specimenTests: [],
      grid: null,
      currentTab: "requested",
      selectionMode: {
        enabled: false
      },
      testForm: {
        specimenId: null,
        cytMolecTestId: null,
        cytMolecTestStatusId: CytMolecTestStatusEnum.Requested
      },
      options: []
    };
  },
  created() {
    this.molecularPanelOptions
      .load({
        sort: { selector: "displayText", desc: false }
      })
      .then(data => {
        this.options = [
          ...this.options,
          ...data.map(e => ({ id: e.id, displayName: `${e.displayText} (P)`, type: "panel" }))
        ].sort((a, b) => a.displayName.localeCompare(b.displayName));
        return this.testOptions.load().then(data => {
          this.options = [
            ...this.options,
            ...data.map(e => ({ id: e.id, displayName: `${e.displayText} (T)`, type: "test" }))
          ].sort((a, b) => a.displayName.localeCompare(b.displayName));
        });
      });
  },
  domStreams: ["contentReady$"],
  subscriptions() {
    const availableResults$ = this.contentReady$.pipe(
      map(({ component }) => {
        const rows = component.getVisibleRows();
        return rows.map(e => e.data.cytMolecTestId);
      }),
      distinctUntilChanged((prev, curr) => {
        return JSON.stringify(prev) === JSON.stringify(curr);
      }),
      switchMap(ids => {
        const setData = new Set(ids);
        return cytologyService.getAvailableResults(Array.from(setData));
      }),
      map(data => {
        return data.reduce((acc, curr) => {
          acc[curr.id] = curr.results;
          return acc;
        }, {});
      })
    );

    const registeredRows = this.contentReady$.pipe(
      map(({ component }) => {
        const rows = component.getVisibleRows();
        return rows.reduce((acc, curr) => {
          acc[curr.data.id] = curr.data;
          return acc;
        }, {});
      })
    );
    return {
      availableResults$,
      registeredRows
    };
  },
  computed: {
    ...mapState({
      specimens: state => state.accessionStore.caseDetails.specimens,
      user: state => state.currentUser
    }),
    specimenId() {
      if (this.specimenIdentifier) {
        return this.specimenIdentifier;
      }
      const specimen = this.specimens[0];
      if (specimen) {
        return specimen.id;
      }
      return null;
    },
    source() {
      return new DataSource({
        store: cytologyService.getLabCytoTests(),
        key: "id",
        filter: ["caseId", "=", this.caseId]
      });
    },
    actionColumn() {
      return {
        dataField: "caseId",
        caption: "Actions",
        cellTemplate: "actionsTemplate"
      };
    },
    baseColumns() {
      return [
        { dataField: "caseNumber" },
        { dataField: "numberPrefix", caption: "Prefix" },
        { dataField: "numberYear", caption: "Year" },
        { dataField: "numberSequence", caption: "Number" },
        { dataField: "requestedBy" },
        { dataField: "patientName", caption: "Patient" },
        { dataField: "cytMolecTest", caption: "Test" },
        { dataField: "cytMolecTestStatus", caption: "Status" }
      ];
    },
    requestedColumns() {
      return [
        ...this.baseColumns,
        {
          dataField: "requestedOn",
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.requestedOn) {
              return formatDatetimeCell(data.requestedOn);
            }
            return "";
          }
        },
        this.actionColumn
      ];
    },
    sentColumns() {
      return [
        ...this.baseColumns,
        {
          dataField: "sentOn",
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.sentOn) {
              return formatDatetimeCell(data.sentOn);
            }
            return "";
          }
        },
        this.actionColumn
      ];
    },
    resultColumn() {
      return {
        dataField: "cytMolecResultId",
        caption: "Result",
        allowEditing: true,
        calculateDisplayValue(data) {
          return data.cytMolecResult;
        },
        calculateFilterExpression(value) {
          return [
            ["cytMolecResult", "startsWith", value],
            ["isDeleted", false]
          ];
        },
        lookup: {
          dataSource: options => {
            console.log("options", options);
            if (options.data) {
              const { cytMolecTestId } = options.data;
              if (cytMolecTestId && this.availableResults$[cytMolecTestId]) {
                return this.availableResults$[cytMolecTestId];
              }
            }
            return [];
          },
          displayExpr: "resultDisplayText",
          valueExpr: "cytMolecResultId"
        }
      };
    },
    resultedColumns() {
      return [
        ...this.baseColumns,
        {
          dataField: "resultedOn",
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.resultedOn) {
              return formatDatetimeCell(data.resultedOn);
            }
            return "";
          }
        },
        this.actionColumn
      ];
    },
    columns() {
      if (this.accession) {
        return this.requestedColumns;
      }
      return [
        ...this.baseColumns,
        this.resultColumn,
        {
          dataField: "sentOn",
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.sentOn) {
              return formatDatetimeCell(data.sentOn);
            }
            return "";
          }
        },
        {
          dataField: "resultedOn",
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.resultedOn) {
              return formatDatetimeCell(data.resultedOn);
            }
            return "";
          }
        },
        this.actionColumn
      ];
    },
    caseId() {
      return this.$route.params.caseId || this.caseIdentifier;
    },
    specimenIds() {
      return (this.specimens || [])?.map(specimen => specimen.id);
    },
    dataSource() {
      return this.specimenTests;
    },
    specimenOptions() {
      return this.specimens.map(specimen => ({
        id: specimen.id,
        displayName: specimen.specimenOrder
      }));
    }
  },
  methods: {
    async initializeGrid({ component }) {
      this.grid = component;
      this.grid.refresh();
    },
    async handleContentReady({ component }) {
      if (this.grid) {
        this.contentReady$.next(component);
      }
    },
    triggerShortkey(e) {
      const { srcKey } = e;
      switch (srcKey) {
        case "a":
          this.toggleModal();
          break;
        case "s": {
          if (this.isModalOpen) {
            e.stopPropagation();
            this.handleAddTest();
          } else {
            this.$emit("shortkey", e);
          }
          break;
        }
      }
    },
    async deleteTest(id) {
      await SpecimensApi.deleteMolecularTest(id);
      this.grid.refresh();
    },
    async editTest(id) {
      const details = await SpecimensApi.getMolecularTest(id);
      this.testForm = details;
      this.toggleModal();
    },
    getSpecimenTests(specimenId) {
      return SpecimensApi.getMolecularTests(specimenId);
    },
    toggleModal() {
      this.isModalOpen = !this.isModalOpen;
      if (!this.isModalOpen) {
        this.targetId = null;
      } else {
        this.$nextTick(() => {
          const selector = this.$refs.selector;
          if (selector) {
            selector.focus();
          }
        });
      }
    },
    async handleAddTest() {
      const { ...rest } = this.testForm;

      const findOption = this.options.find(e => e.id === this.targetId);
      if (!findOption) {
        window.alert("Please select a test or panel.");
        return;
      }

      const specimenId = this.specimenIds[0];
      const { type, id } = findOption;
      try {
        this.isLoading = true;
        if (type === "test") {
          const payload = {
            ...rest,
            cytMolecTestId: id,
            specimenId,
            requestedBy: this.user.displayName,
            requestedOn: new Date(),
            cytMolecTestStatusId: CytMolecTestStatusEnum.Requested
          };
          await SpecimensApi.insertMolecularTest(specimenId, payload);
        }
        if (type === "panel") {
          const payload = {
            specimenId: specimenId,
            cytMolecPanelId: id,
            requestedBy: this.user.displayName,
            requestedOn: new Date()
          };
          await cytologyService.addPanel(specimenId, payload);
        }
        //! If inside a case dispatch an event to refresh quicklinks
        this.grid.refresh();
        this.$store.dispatch("accessionStore/getCaseQuickLinks", this.caseId).catch(error => {
          console.error("Error refreshing quick links after cyto test:", error);
        });
        this.toggleModal();
      } catch (error) {
        console.error(error);
        window.notify("An error occurred while adding the test(s).", "error");
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
}
.footer {
  text-align: right;
}
</style>
